import { useState } from 'react';

interface TrialOfferSectionProps {
  onCtaClick?: () => void;
}

const inclusions = [
  {
    icon: 'ri-calendar-2-line',
    title: '4 Wochen Training',
    text: 'Vier persönlich betreute EMS-Einheiten. Einmal pro Woche. Komplett strukturiert.',
  },
  {
    icon: 'ri-user-heart-line',
    title: '1:1-Betreuung',
    text: 'Dein persönlicher Trainer begleitet jede Einheit – vom ersten bis zum letzten Atemzug.',
  },
  {
    icon: 'ri-time-line',
    title: '20 Minuten pro Einheit',
    text: 'Nicht mehr. Passend für jeden Alltag, jeden Beruf, jede Lebenssituation.',
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Individueller Einstieg',
    text: 'Kein generisches Programm. Deine Ziele, dein Körper, dein Tempo.',
  },
  {
    icon: 'ri-shield-user-line',
    title: 'Angenehme Studioatmosphäre',
    text: 'Kein Vergleich. Kein Lärm. Kein Leistungsdruck. Du trainierst in Ruhe.',
  },
  {
    icon: 'ri-checkbox-circle-line',
    title: 'Vollständig unverbindlich',
    text: 'Kein Abo, keine Unterschrift, keine versteckten Bedingungen.',
  },
];

const objections = [
  { icon: 'ri-seedling-line', label: 'Auch als Einsteiger ohne Vorkenntnisse' },
  { icon: 'ri-time-line', label: 'Auch bei sehr wenig Zeit im Alltag' },
  { icon: 'ri-body-scan-line', label: 'Auch bei Rücken- oder Alltagsbeschwerden' },
  { icon: 'ri-scales-3-line', label: 'Auch wenn du lange kein Training hattest' },
  { icon: 'ri-heart-pulse-line', label: 'Auch wenn du ein konkretes Figurziel hast' },
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  goal: string;
}

export default function TrialOfferSection({ onCtaClick: _onCtaClick }: TrialOfferSectionProps) {
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', goal: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    body.append('aktion', '4-Wochen-Testaktion');
    try {
      await fetch('https://readdy.ai/api/form/d7b2pbrurf8q17puvn4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const focusStyle = { borderColor: '#1A5C3D', outline: 'none' };
  const blurStyle = { borderColor: '#e5e7eb' };

  return (
    <section id="vier-wochen" className="py-24 md:py-36 relative overflow-hidden" style={{ backgroundColor: '#F2F6F3' }}>
      {/* Subtle background accent */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(26,92,61,0.05) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
      />

      <div className="max-w-6xl mx-auto px-6 relative z-10">

        {/* Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="flex items-center gap-3 mb-6">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-medium tracking-[0.15em] uppercase"
              style={{ borderColor: 'rgba(26,92,61,0.25)', color: '#1A5C3D', backgroundColor: 'rgba(26,92,61,0.06)', fontFamily: 'Inter, sans-serif' }}
            >
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#1A5C3D' }} />
              Exklusives Angebot für Pinneberg
            </div>
          </div>

          <h2
            className="text-4xl md:text-[58px] font-bold text-gray-900 leading-[1.05] tracking-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            4 Wochen testen.<br />
            <em className="not-italic" style={{ color: '#1A5C3D' }}>Einfach anfangen.</em>
          </h2>

          <p
            className="text-gray-600 text-lg md:text-xl leading-relaxed max-w-2xl"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            Du hast wenig Zeit, bist im normalen Fitnessstudio nie richtig angekommen
            oder weißt schlicht nicht, wo du anfangen sollst? Dann ist das hier für dich gemacht.
          </p>
          <p
            className="text-gray-500 text-base mt-4 leading-relaxed max-w-xl"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            4 Wochen. 4 Einheiten. Persönlich betreut. Kein leeres Versprechen –
            ein echter Eindruck davon, was EMS-Training für deinen Körper tun kann.
          </p>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">

          {/* Left: Leistungen + Einwandabbau */}
          <div>
            <h3
              className="text-gray-900 font-semibold text-xl mb-8"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Was du in 4 Wochen bekommst
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
              {inclusions.map((item, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-5 border transition-all duration-200 hover:border-green-200 group"
                  style={{ borderColor: 'rgba(26,92,61,0.1)' }}
                >
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center mb-3 flex-shrink-0"
                    style={{ backgroundColor: 'rgba(26,92,61,0.08)' }}
                  >
                    <i className={`${item.icon} text-base`} style={{ color: '#1A5C3D' }}></i>
                  </div>
                  <div className="text-gray-900 font-semibold text-sm mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {item.title}
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Einwandabbau */}
            <div
              className="rounded-2xl p-6 border-l-4"
              style={{ backgroundColor: '#1A5C3D', borderLeftColor: '#5DC48E' }}
            >
              <h4
                className="text-white font-semibold text-base mb-5"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Das funktioniert auch für dich –
              </h4>
              <div className="space-y-3">
                {objections.map((o, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${o.icon} text-sm`} style={{ color: '#6ED8A0' }}></i>
                    </div>
                    <span
                      className="text-sm"
                      style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.8)' }}
                    >
                      {o.label}
                    </span>
                  </div>
                ))}
              </div>
              <div
                className="mt-6 pt-5 border-t"
                style={{ borderColor: 'rgba(255,255,255,0.12)' }}
              >
                <p
                  className="text-sm leading-relaxed italic"
                  style={{ fontFamily: 'Cormorant Garamond, serif', color: 'rgba(255,255,255,0.65)', fontSize: '16px' }}
                >
                  „Wir haben noch niemanden nach Hause geschickt, weil er ‚nicht gut genug'
                  für EMS war. Das gibt es nicht."
                </p>
                <p
                  className="text-xs mt-2"
                  style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.35)' }}
                >
                  — Das Team von Körperformen Pinneberg
                </p>
              </div>
            </div>
          </div>

          {/* Right: CTA Box */}
          <div className="lg:sticky lg:top-28">
            <div
              className="rounded-3xl overflow-hidden border"
              style={{ borderColor: 'rgba(26,92,61,0.18)', backgroundColor: '#fff' }}
            >
              {/* Top accent bar */}
              <div
                className="h-1.5 w-full"
                style={{ background: 'linear-gradient(90deg, #1A5C3D 0%, #5DC48E 100%)' }}
              />

              <div className="p-8 md:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center text-center py-10">
                    <div
                      className="w-16 h-16 flex items-center justify-center rounded-full mb-5"
                      style={{ backgroundColor: 'rgba(26,92,61,0.1)' }}
                    >
                      <i className="ri-check-line text-3xl" style={{ color: '#1A5C3D' }}></i>
                    </div>
                    <h3 className="text-gray-900 text-2xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Perfekt. Wir freuen uns.
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-xs" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                      Deine Anfrage ist angekommen. Wir melden uns persönlich
                      innerhalb von 24 Stunden – kein Standardtext, kein Autoresponder.
                    </p>
                  </div>
                ) : !showForm ? (
                  <>
                    <div className="mb-6">
                      <div
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4"
                        style={{ backgroundColor: 'rgba(26,92,61,0.07)', color: '#1A5C3D', fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-gift-line text-xs"></i>
                        Limitiertes Einstiegsangebot
                      </div>
                      <h3 className="text-gray-900 text-2xl font-bold leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                        4 Wochen testen –<br />
                        <span style={{ color: '#1A5C3D' }}>unverbindlich einsteigen.</span>
                      </h3>
                      <p className="text-gray-500 text-sm mt-3 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                        Vier Einheiten mit persönlicher Betreuung. Kein Abo, keine Vertragsbindung.
                        Du entscheidest danach – vollständig frei.
                      </p>
                    </div>

                    <div className="space-y-3 mb-7">
                      {[
                        '4 × 20 Min. Training, jede Einheit persönlich betreut',
                        'Individuelles Einsteigerprogramm für dich',
                        'Diskrete, ruhige Studioatmosphäre in Pinneberg',
                        'Kein Folgevertrag, kein Kleingedrucktes',
                        'Parkplatz direkt vor dem Studio',
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div
                            className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: 'rgba(26,92,61,0.1)' }}
                          >
                            <i className="ri-check-line text-xs" style={{ color: '#1A5C3D' }}></i>
                          </div>
                          <span className="text-gray-700 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => setShowForm(true)}
                      className="w-full text-white font-semibold text-sm py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap"
                      style={{ backgroundColor: '#1A5C3D', fontFamily: 'Inter, sans-serif' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1E6B47')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1A5C3D')}
                    >
                      4 Wochen unverbindlich testen →
                    </button>
                    <p className="text-center text-gray-400 text-xs mt-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Anfrage dauert 60 Sekunden · Kostenlos · Kein Druck
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mb-6">
                      <button
                        onClick={() => setShowForm(false)}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer mb-4"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        <i className="ri-arrow-left-line text-sm"></i>
                        Zurück
                      </button>
                      <h3 className="text-gray-900 text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                        4-Wochen-Testaktion anfragen
                      </h3>
                      <p className="text-gray-400 text-xs mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                        60 Sekunden · kostenlos · unverbindlich
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                          Dein Name *
                        </label>
                        <input
                          type="text" name="name" required value={form.name} onChange={handleChange}
                          placeholder="Vorname Nachname"
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 transition-colors"
                          style={{ fontFamily: 'Inter, sans-serif', outline: 'none' }}
                          onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                          onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                          E-Mail *
                        </label>
                        <input
                          type="email" name="email" required value={form.email} onChange={handleChange}
                          placeholder="deine@email.de"
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 transition-colors"
                          style={{ fontFamily: 'Inter, sans-serif', outline: 'none' }}
                          onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                          onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                          Telefon
                        </label>
                        <input
                          type="tel" name="phone" value={form.phone} onChange={handleChange}
                          placeholder="+49 ..."
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 transition-colors"
                          style={{ fontFamily: 'Inter, sans-serif', outline: 'none' }}
                          onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                          onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                          Dein wichtigstes Ziel
                        </label>
                        <select
                          name="goal" value={form.goal} onChange={handleChange}
                          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm cursor-pointer transition-colors"
                          style={{ fontFamily: 'Inter, sans-serif', outline: 'none' }}
                          onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                          onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                        >
                          <option value="">Bitte wählen ...</option>
                          <option value="Abnehmen / Figur verbessern">Abnehmen &amp; Figur</option>
                          <option value="Kraft und Fitness aufbauen">Kraft &amp; Fitness</option>
                          <option value="Rücken- oder Beschwerden lindern">Rücken- &amp; Gelenkbeschwerden</option>
                          <option value="Gesundheit und Wohlbefinden">Gesundheit &amp; Energie</option>
                          <option value="EMS kennenlernen">EMS einfach kennenlernen</option>
                        </select>
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full text-white font-semibold text-sm py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 mt-2"
                        style={{ backgroundColor: loading ? '#999' : '#1A5C3D', fontFamily: 'Inter, sans-serif' }}
                        onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#236B48'; }}
                        onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#1A5C3D'; }}
                      >
                        {loading ? 'Wird gesendet …' : '4 Wochen unverbindlich testen →'}
                      </button>
                      <p className="text-center text-gray-400 text-xs leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Kein Abo. Keine Unterschrift. Rückmeldung in 24 Std.
                      </p>
                    </form>
                  </>
                )}
              </div>

              {/* Trust footer */}
              {!submitted && (
                <div
                  className="px-8 md:px-10 py-4 border-t flex flex-wrap items-center gap-4"
                  style={{ backgroundColor: '#F7FAF8', borderColor: 'rgba(26,92,61,0.1)' }}
                >
                  {[
                    { icon: 'ri-shield-check-line', text: 'Kein Verkaufsdruck' },
                    { icon: 'ri-map-pin-line', text: 'Pinneberg, mit Parkplatz' },
                    { icon: 'ri-star-fill', text: '5,0 ★ Google' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`${item.icon} text-xs`} style={{ color: '#1A5C3D' }}></i>
                      </div>
                      <span className="text-xs" style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280' }}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
